/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {MutableRefObject, useEffect, useRef, useState} from "react";
import {useTypedSelector} from "helpers/reducers";
import {RouteComponentProps, withRouter} from "react-router-dom";
import {PackageCategoryDetailEnum} from "types/enums/PackageCategoryDetailEnum";
import PackageCategoryCard from "pages/Front/Booking/components/PackageCategoryCard";
import Loader from "components/Loader";
import {usePackageCategory} from "utils/hooks/usePackageCategory";
import {PackageCategoryEntity} from "types/entities/PackageCategoryEntity";
import {useTranslation} from "react-i18next";
import {PackageCategory} from "types/enums/PackageCategory";
import {PackageItemItemType} from "types/enums/PackageItemItemType";
import {PackageItem} from "types/entities/PackageItem";
import PackageCategoryDetailUpsell
    from "pages/Front/Booking/components/PackageCategoryDetail/components/PackageCategoryDetailUpsell";
import CategoryDetailPrices from "pages/Front/Booking/components/PackageCategoryDetail/components/CategoryDetailPrices";
import CategoryDetailPriceImages
    from "pages/Front/Booking/components/PackageCategoryDetail/components/CategoryDetailPriceImages";
import CategoryDetailInfoCards
    from "pages/Front/Booking/components/PackageCategoryDetail/components/CategoryDetailInfoCards";
import CategoryDetailEventPackage
    from "pages/Front/Booking/components/PackageCategoryDetail/components/CategoryDetailEventPackage";
import CategoryDetailFooterButton
    from "pages/Front/Booking/components/PackageCategoryDetail/components/CategoryDetailFooterButton";
import clsx from "clsx";

interface PackageCategoryDetailParams {
    categorySlug?: string;
}

interface PackageCategoryDetailProps extends RouteComponentProps<PackageCategoryDetailParams> {
    type: PackageCategoryDetailEnum;
}

const PackageCategoryDetail = (props: PackageCategoryDetailProps) => {
    const {t} = useTranslation();
    const [packageCategoryEntity, setPackageCategoryEntity] = useState<PackageCategoryEntity | null>(null);
    const [upsells, setUpsells] = useState<PackageItem[]>([]);
    const packages = useTypedSelector(state => state.bookingState.packages);
    const {getCategoryBySlug, packageCategoryCardHandle} = usePackageCategory();
    const isEvent: boolean = props.type === PackageCategoryDetailEnum.EventDetail;
    const elementRef = useRef<HTMLDivElement | null>(null);
    const bottomElementRef = useRef<HTMLDivElement | null>(null);
    const [isCategoryCardVisible, setIsCategoryCardVisible] = useState<boolean>(true);
    const [isCategoryCardOnTheBottom, setIsCategoryCardOnTheBottom] = useState<boolean>(true);
    const competitionsIdsHide = [3883, 3884, 3885, 3886, 3888, 3889, 3891, 3892, 3893, 3894, 3895, 3896, 3897, 3887, 3898, 3899, 3900, 3901, 3902, 3903, 3904, 3905];

    useEffect(() => {
        if (props.match.params?.categorySlug) {
            const category = getCategoryBySlug(props.match.params.categorySlug as PackageCategory);
            if (category) {
                setPackageCategoryEntity(category);
            }

            // upsells
            if (isEvent) {
                const packagesInCategory = packages.filter(item => item.category === props.match.params?.categorySlug);
                const packageItems = packagesInCategory
                    .map(item => item.packageItems.filter(innerItem => innerItem.type === PackageItemItemType.Upsell))
                    .flat();
                const uniquePackageItems = packageItems.reduce((uniqueItems, currentItem) => {
                    if (!uniqueItems.some(item => item.article_id === currentItem.article_id)) {
                        uniqueItems.push(currentItem);
                    }
                    return uniqueItems;
                }, [] as PackageItem[]);
                const sortedUpsells = uniquePackageItems
                    .filter(item => item.is_visible != 0 && item.is_active != 0)
                    .sort((a, b) => {
                        const positionA = a.position ?? Number.MAX_SAFE_INTEGER;
                        const positionB = b.position ?? Number.MAX_SAFE_INTEGER;
                        return positionA - positionB;
                    });
                setUpsells(sortedUpsells);
            }
        }
    }, []);

    const observer = (element: MutableRefObject<HTMLDivElement | null>, callBack: (visible: boolean) => void) => {
        const currentElement = element.current;

        const observer = new IntersectionObserver(
            ([entry]) => {
                callBack(entry.isIntersecting);
            },
            {
                root: currentElement,
                rootMargin: "0px",
                threshold: 0,
            } as IntersectionObserverInit
        );

        if (currentElement) {
            observer.observe(currentElement);
        } else {
            setTimeout(() => {
                const delayedElement = element.current;
                if (delayedElement) {
                    observer.observe(delayedElement);
                }
            }, 100);
        }

        return () => {
            if (currentElement) {
                observer.unobserve(currentElement);
            }
        };
    };

    useEffect(() => {
        return observer(elementRef, setIsCategoryCardVisible);
    }, []);

    useEffect(() => {
        return observer(bottomElementRef, setIsCategoryCardOnTheBottom);
    }, []);

    if (!packageCategoryEntity) {
        return <Loader />;
    }

    return (
        <div className={"PackageCategoryDetail"}>
            <div className={"grid grid-cols-12 gap-y-12 md:gap-y-16 lg:gap-y-24 gap-x-4 md:gap-x-6 lg:gap-x-8 xl:gap-x-12"}>
                <div ref={elementRef} className={"PackageCategoryCardHolder col-span-12"}>
                    <PackageCategoryCard
                        categoryEntity={packageCategoryEntity}
                        className={"w-full"}
                        index={0}
                        showMoreDetail={false}
                        onClick={() => packageCategoryCardHandle(packageCategoryEntity.id)}
                        onDetailClick={() => {}}
                    />
                </div>

                <CategoryDetailPrices timeAvailableText={packageCategoryEntity.timeAvailableText} />

                {packageCategoryEntity.priceImages && <CategoryDetailPriceImages priceImages={packageCategoryEntity.priceImages} />}

                {packageCategoryEntity.categoryInfo && <CategoryDetailInfoCards categoryInfo={packageCategoryEntity.categoryInfo} />}

                {packageCategoryEntity.categoryInfo?.infoEventPackage && <CategoryDetailEventPackage packageCategoryEntity={packageCategoryEntity} />}

                {isEvent && upsells.length > 0 && (
                    <section className={"PackageCategoryDetailUpsells col-span-12"}>
                        <div className={"flex flex-col gap-2 md:gap-4 font-poppins mb-8"}>
                            <h2 className={"font-zuume italic font-black text-5xl md:text-6xl text-black text-center"}>{t("Extras")}</h2>
                            {packageCategoryEntity.categoryInfo && (
                                <h3 className={"font-semibold leading-normal text-gray-600 text-lg text-center"}>
                                    {packageCategoryEntity.categoryInfo.upsell_percent > 0 ? (
                                        <span>
                                            {t("Benefit from PERCENT discount on our selection of additional extras.", {
                                                percent: packageCategoryEntity.categoryInfo?.upsell_percent ?? 5,
                                            })}
                                        </span>
                                    ) : (
                                        <span>{t("We have a selection of additional extras to help you get the most out of your experience.")}</span>
                                    )}
                                </h3>
                            )}
                        </div>
                        <div className={"grid grid-cols-12 gap-4 md:gap-6 lg:gap-8 xl:gap-12 w-full pb-20"}>
                            {upsells
                                .filter((u) => !competitionsIdsHide.includes(u.id))
                                .map((upsell, index) => (
                                <PackageCategoryDetailUpsell
                                    key={index}
                                    className={"col-span-12 xs:col-span-6 md:col-span-4 lg:col-span-3"}
                                    packageItem={upsell}
                                />
                            ))}
                        </div>
                    </section>
                )}
            </div>

            <div className={clsx("relative w-full h-[120px] xs:h-[90px] md:h-[110px] lg:h-[140px] xl:h-[160px]")}>
                <div ref={bottomElementRef} className={"w-full h-[0px] absolute bottom-0 left-0 right-0"} />
            </div>

            <CategoryDetailFooterButton
                isCategoryCardOnTheBottom={isCategoryCardOnTheBottom}
                isCategoryCardVisible={isCategoryCardVisible}
                packageCategoryEntity={packageCategoryEntity}
                onClick={() => packageCategoryCardHandle(packageCategoryEntity.id)}
            />
        </div>
    );
};

export default withRouter(PackageCategoryDetail);
